import {Divider, Stack} from "@mui/material";
import {useFormContext, useWatch} from "react-hook-form";
import {LoadingButton} from "@mui/lab";
import Iconify from "../../../components/Iconify";
import {DIRECTION_NAME, INVOICE_TYPE, OPERATORS_GROUP, PAYMENT_TYPE, SERBIA_ALPHA_2_CODE} from "../../../constants";
import AmountDetails from "../InvoiceForm/AmountDetails";
import Passenger from "../InvoiceForm/Passenger";
import Services from "../InvoiceForm/Services";
import {RHFTextField} from "../../../components/hook-form";
import {onSubmitWithOppositeSplit} from "../utils/onSubmitWithOppositeSplite";
import {useCallback, useEffect} from "react";
import {useSelector} from "../../../redux/store";
import moment from "moment";
//import PassengerDetails from "../../SalePage/PassengerDetails";

export default function TripExpandedDetails({
                                                data,
                                                directionType,
                                                onSubmit,
                                                enqueueSnackbar,
                                                handleOpenPaymentDialog,
                                                translate,
                                                OPERATOR,
                                            }) {
    const {control, setValue, getValues, handleSubmit} = useFormContext();
    const {a4PrintEnabled, canSellExpiredDepartures, expiredDepartureMinutesLimit} = useSelector(state => state.userSettings);
    const {settings} = useSelector(state => state.settings);

    const disableSalePriceZero = directionType === DIRECTION_NAME[1] ? data.priceReturnTicket === 0 : data.priceOneWay === 0;
    const canIssueTraining = [OPERATORS_GROUP.admin, OPERATORS_GROUP.superAdmin, OPERATORS_GROUP.dispatcher]
        .includes(OPERATOR?.group);
    const passengers = useWatch({control, name: "passengers"});

    const validatePassengerPrivileges = (passengers) => {
        return passengers.every((p) => {
            const requiresValidation = p.discountPCT?.documentValidation;
            return !requiresValidation || p.havePrivilege === true;
        });
    };

    const isTripExpired = () => {
        if (data.canceled) return true;
        const departureTime = moment.tz(data.departureTripTime, "YYYY-MM-DD HH:mm:ss", "Europe/Belgrade");
        const now = moment.tz("Europe/Belgrade");
        if (departureTime.isAfter(now)) return false;
        if (!canSellExpiredDepartures) return true;
        const minutesLimit = expiredDepartureMinutesLimit || 0;
        const minutesSinceDeparture = now.diff(departureTime, 'minutes', true);
        return minutesSinceDeparture > minutesLimit;
    };

    const validateAndSubmit = (func) => {
        if (isTripExpired()) {
            return enqueueSnackbar("Polazak je istekao!", {variant: "warning"});
        }
        if (!validatePassengerPrivileges(passengers)) {
            return enqueueSnackbar("Privilegovani putnici moraju imati izabranu povlasticu", {
                variant: "info"
            });
        }
        if (data.externalSale === true) {
            const requiredFields = [
                {value: "emailForTicket", message: "Email za kartu je obavezan!"},
                {value: "phoneNumber", message: "Broj telefona je obavezan!"},
            ]
            for (const requiredField of requiredFields) {
                let value = getValues(requiredField.value);
                if (!value || value?.trim() === "") {
                    return enqueueSnackbar(requiredField.message, {
                        variant: "info"
                    });
                }
            }
            const flixFields = getValues("flixFields");
            const passengers = getValues("passengers");
            for (let i = 0; i < passengers.length; i++) {
                for (const flixField of flixFields) {
                    let passenger = passengers[i];
                    if (!passenger[flixField.value] || passenger[flixField.value]?.trim() === "") {
                        return enqueueSnackbar(`Polje: ${flixField.name} je obavezno za putnika ${i + 1}`, {
                            variant: "info"
                        });
                    }
                }
            }
        }

        const returnDate = getValues("returnDate");
        const returnTrip = getValues("returnTrip");
        if (
            data.requiresReturnDate &&
            directionType === DIRECTION_NAME[1] &&
            !returnDate &&
            !returnTrip
        ) {
            return enqueueSnackbar("Datum i vreme polaska su obavezni", {variant: "warning"});
        }
        const isInternational = data?.arrCountryCode !== SERBIA_ALPHA_2_CODE || data?.depCountryCode !== SERBIA_ALPHA_2_CODE;
        if (settings?.requireNamesForInternationalTicket && isInternational) {
            const currentPassengers = getValues("passengers") || [];
            const missing = currentPassengers.some(p => !p?.name?.trim() || !p?.lastName?.trim());
            if (missing) {
                return enqueueSnackbar("Za međunarodne karte obavezno je popuniti ime i prezime za svakog putnika", {variant: "warning"});
            }
        }
        func();
    };

    const cashQuick = useCallback(handleSubmit((props) =>
        validateAndSubmit(() =>
            onSubmitWithOppositeSplit(props, PAYMENT_TYPE.cash, INVOICE_TYPE.normal).then(onSubmit)
        )
    ), [handleSubmit, validateAndSubmit, onSubmit]);

    const cashA4 = useCallback(handleSubmit((props) =>
        validateAndSubmit(() =>
            onSubmitWithOppositeSplit(props, PAYMENT_TYPE.cash, INVOICE_TYPE.normal).then((data) => onSubmit({...data, isA4Print: true}))
        )
    ), [handleSubmit, validateAndSubmit, onSubmit]);

    const cardQuick = useCallback(handleSubmit((props) =>
        validateAndSubmit(() =>
            onSubmitWithOppositeSplit(props, PAYMENT_TYPE.card, INVOICE_TYPE.normal).then(onSubmit)
        )
    ), [handleSubmit, validateAndSubmit, onSubmit]);

    const cardA4 = useCallback(handleSubmit((props) =>
        validateAndSubmit(() =>
            onSubmitWithOppositeSplit(props, PAYMENT_TYPE.card, INVOICE_TYPE.normal).then((data) => onSubmit({...data, isA4Print: true}))
        )
    ), [handleSubmit, validateAndSubmit, onSubmit]);

    const fiscalize = useCallback(handleSubmit(() =>
        validateAndSubmit(() =>
            handleOpenPaymentDialog(data, INVOICE_TYPE.normal)
        )
    ), [handleSubmit, validateAndSubmit, handleOpenPaymentDialog, data]);

    useEffect(() => {
        const isTypingElement = (el) => {
            const tag = el?.tagName;
            return el?.isContentEditable ||
                tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
        };

        const onKey = (e) => {
            // Ne prekidaj kucanje u poljima
            if (isTypingElement(e.target)) return;

            // + (radi i na numpadu)
            if (e.key === '+' || e.code === 'NumpadAdd') {
                e.preventDefault();
                if (disableSalePriceZero) {
                    enqueueSnackbar("Prodaja nije dozvoljena kada je cena 0.", { variant: "info" });
                    return;
                }
                cashQuick();
                return;
            }

            // - (radi i na numpadu)
            if (e.key === '-' || e.code === 'NumpadSubtract') {
                e.preventDefault();
                if (disableSalePriceZero) {
                    enqueueSnackbar("Prodaja nije dozvoljena kada je cena 0.", { variant: "info" });
                    return;
                }
                cardQuick();
                return;
            }

            // * (radi i na numpadu)
            if (e.key === '*' || e.code === 'NumpadMultiply') {
                e.preventDefault();
                fiscalize();
                return;
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [cashQuick, cardQuick, fiscalize, disableSalePriceZero, enqueueSnackbar]);

    const handleRemoveReturnTrip = () => {
        setValue("showReturnDialog", false);
        setValue("returnTrip", null);
        setValue("isOpenIndex", -1);
        setValue("invoiceLine", null);
    };

    // TODO VRATITI RETURN TIP
    return (
        <>
            {/*{returnTrip && (*/}
            {/*    <ReturnTripCard trip={returnTrip} onClose={handleRemoveReturnTrip}/>*/}
            {/*)}*/}

            <Divider sx={{my: 2}}/>

            <AmountDetails
                state={data}
                directionType={directionType}
                total={0}
                discount={0}
            />

            <Stack
                sx={{mb: 2, mt: 1}}
                direction="row"
                spacing={1.2}
                alignItems="center"
                justifyContent="center"
            >
                <LoadingButton
                    sx={{width: 180, py: 2}}
                    startIcon={<Iconify icon="solar:copy-bold-duotone"/>}
                    color="inherit"
                    variant="contained"
                    onClick={handleSubmit(() =>
                        handleOpenPaymentDialog({...data, confirmation: true}, INVOICE_TYPE.normal, true)
                    )}
                >
                    Potvrda
                </LoadingButton>

                {canIssueTraining && (
                    <LoadingButton
                        sx={{width: 180, py: 2}}
                        startIcon={<Iconify icon="solar:copy-bold-duotone"/>}
                        color="inherit"
                        variant="contained"
                        disabled={disableSalePriceZero}
                        onClick={handleSubmit(() =>
                            validateAndSubmit(() =>
                                handleOpenPaymentDialog(data, INVOICE_TYPE.training)
                            )
                        )}
                    >
                        Obuka
                    </LoadingButton>
                )}

                <LoadingButton
                    sx={{width: 180, py: 2}}
                    startIcon={<Iconify icon="solar:printer-bold-duotone"/>}
                    color="primary"
                    variant="contained"
                    disabled={disableSalePriceZero}
                    onClick={handleSubmit((props) =>
                        validateAndSubmit(() =>
                            onSubmitWithOppositeSplit(props, PAYMENT_TYPE.cash, INVOICE_TYPE.normal).then(onSubmit))
                    )}
                >
                    Gotovina brzi
                </LoadingButton>

                {a4PrintEnabled && (
                    <LoadingButton
                        sx={{width: 180, py: 2}}
                        startIcon={<Iconify icon="solar:document-bold-duotone"/>}
                        color="secondary"
                        variant="contained"
                        disabled={disableSalePriceZero}
                        onClick={() => cashA4()}
                    >
                        Gotovina A4
                    </LoadingButton>
                )}

                <LoadingButton
                    sx={{width: 180, py: 2}}
                    startIcon={<Iconify icon="solar:printer-bold-duotone"/>}
                    color="primary"
                    variant="contained"
                    disabled={disableSalePriceZero}
                    onClick={handleSubmit((props) =>
                        validateAndSubmit(() =>
                            onSubmitWithOppositeSplit(props, PAYMENT_TYPE.card, INVOICE_TYPE.normal).then(onSubmit))
                    )}
                >
                    Kartica brzi
                </LoadingButton>

                {a4PrintEnabled && (
                    <LoadingButton
                        sx={{width: 180, py: 2}}
                        startIcon={<Iconify icon="solar:document-bold-duotone"/>}
                        color="secondary"
                        variant="contained"
                        disabled={disableSalePriceZero}
                        onClick={() => cardA4()}
                    >
                        Kartica A4
                    </LoadingButton>
                )}

                <LoadingButton
                    sx={{width: 180, py: 2}}
                    startIcon={<Iconify icon="solar:printer-bold-duotone"/>}
                    color="primary"
                    variant="contained"
                    disabled={disableSalePriceZero}
                    onClick={handleSubmit((props) =>
                        validateAndSubmit(() =>
                            handleOpenPaymentDialog(data, INVOICE_TYPE.normal)
                        )
                    )}
                >
                    Fiskalizuj
                </LoadingButton>
            </Stack>

            <Divider sx={{borderStyle: "dashed"}}/>

            {data.externalSale && (
                <Stack sx={{mt: 2}} justifyContent="center" alignItems="center" direction="row" spacing={1.2}>
                    <RHFTextField
                        label={translate("emailForTicket")}
                        sx={{maxWidth: 400}}
                        name="emailForTicket"
                    />
                    <RHFTextField
                        label={`${translate("phoneNumber")} ${data.externalSale ? "(+381xxxxxx)" : ""}`}
                        sx={{maxWidth: 400}}
                        name="phoneNumber"
                    />
                </Stack>
            )}

            <Passenger
                tripData={data}
                open={true}
            />

            <Services/>
        </>
    );
}
